import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * PendingBadge - Displays count of pending changes for admin users
 *
 * Features:
 * - Real-time count updates using Firestore listener
 * - Only visible to admin users
 * - Clickable to navigate to change approval page
 */

export const PendingBadge: React.FC = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);

  // ===== Effects =====

  useEffect(() => {
    // Only admins see pending changes
    if (!authState.user || authState.user.role !== 'admin') {
      return;
    }

    // Real-time listener for pending changes
    const q = query(
      collection(db, 'changes'),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setPendingCount(snapshot.size);
      },
      (error) => {
        console.error('Error listening to pending changes:', error);
      }
    );

    return unsubscribe;
  }, [authState.user]);

  // ===== Event Handlers =====

  const handleClick = () => {
    navigate('/admin/change-approval');
  };

  // ===== Render =====

  // Don't render for non-admins
  if (!authState.user || authState.user.role !== 'admin') {
    return null;
  }

  return (
    <Tooltip title={`${pendingCount} pending change${pendingCount !== 1 ? 's' : ''}`}>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={pendingCount} color="warning">
          <NotificationsIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};
